import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import PropTypes from 'prop-types';
// Material UI
import { makeStyles } from '@material-ui/core/styles';
import MuiCard from '@material-ui/core/Card';
import MuiCardHeader from '@material-ui/core/CardHeader';
import MuiCardContent from '@material-ui/core/CardContent';
import MuiCardActions from '@material-ui/core/CardActions';
import MuiCollapse from '@material-ui/core/Collapse';
// Custom
import AppIcon from 'components/icons/AppIcon';
import Avatar from './Avatar';
import { IconButton } from './Button';
import Typography from './Typography';

const useStyles = (defaultBackground, padding, withTitle, stickyHeader, denseHeader, defaultHeader, rootPaddingX, stretch, isExpandable) => makeStyles((theme) => {
  // Root
  let root = {};
  if (defaultBackground) {
    root = {
      ...root,
      backgroundColor: theme.palette.background.default,
    };
  }
  if (rootPaddingX) {
    root = {
      ...root,
      paddingLeft: rootPaddingX,
      paddingRight: rootPaddingX,
    };
  }

  if (stretch) {
    root = {
      ...root,
      alignSelf: 'stretch',
    };
  }

  // Header
  let header = {
    height: 64,
    cursor: isExpandable ? 'pointer' : 'default',
  };
  if (stickyHeader) {
    const { top } = stickyHeader;
    header = {
      ...header,
      position: 'sticky',
      zIndex: 2,
      backgroundColor: theme.palette.background.paper,
      top,
      left: 0,
    };
  }
  if (denseHeader) {
    header = {
      ...header,
      height: '100%',
      padding: 0,
    };
  }
  if (defaultHeader) {
    header = {
      ...header,
      height: 'auto',
      padding: 0,
    };
  }

  // Expand button
  const expand = {
    marginLeft: 'auto',
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  };
  const expandOpen = {
    transform: 'rotate(180deg)',
  };
    // Content
  let content = {};
  if (withTitle) {
    content = {
      ...content,
      paddingTop: 0,
    };
  }
  if (padding === 'none') {
    content = {
      ...content,
      padding: '0px !important',
    };
  } else if (padding === 'topOnly') {
    content = {
      ...content,
      paddingLeft: 0,
      paddingRight: 0,
      paddingBottom: '0px !important',
    };
  } else if (padding === 'disableTop') {
    content = {
      ...content,
      paddingTop: 0,
    };
  } else if (padding === 'dense') {
    content = {
      ...content,
      padding: '8px !important',
    };
  }

  return {
    root,
    header,
    expand,
    expandOpen,
    content,
  };
});

const Card = ({
  defaultBackground,
  padding,
  outlined,
  icon,
  title,
  subtitle,
  stickyHeader,
  denseHeader,
  defaultHeader,
  headerAvatar,
  headerAction,
  actions,
  expandFromActions,
  disableActionsSpacing,
  contentOptions,
  children,
  onExpand,
  rootPaddingX,
  stretch,
  ...otherProps
}) => {
  // Content options
  const { expandable: contentExpandable, expanded: contentExpanded } = contentOptions;

  const classes = useStyles(defaultBackground, padding, !!title, stickyHeader, denseHeader, defaultHeader, rootPaddingX, stretch, contentExpandable)();

  // Manage expanded state
  const [expanded, setExpanded] = React.useState(contentExpanded);
  const handleExpand = () => {
    if (contentExpandable) {
      setExpanded(!expanded);
      if (onExpand) {
        onExpand(!expanded);
      }
    }
  };

  // Components props
  let cardProps = { ...otherProps };
  if (outlined) {
    cardProps = {
      ...cardProps,
      variant: 'outlined',
    };
  }

  let cardTitle;
  if (title) {
    if (title.type === (<Typography />).type) {
      cardTitle = title;
    } else {
      cardTitle = <Typography type="cardTitle">{title}</Typography>;
    }
  }

  let cardHeaderProps = {
    avatar: headerAvatar,
    title: cardTitle,
    subheader: subtitle,
    action: contentExpandable ? (
      <IconButton
        className={classnames(classes.expand, {
          [classes.expandOpen]: expanded,
        })}
        iconId="btnChevronDown"
        onClick={handleExpand}
      />
    ) : (
      headerAction
    ),
  };
  if (icon) {
    const { id: iconId, primary, color } = icon;
    cardHeaderProps = {
      ...cardHeaderProps,
      avatar: (
        <Avatar size={32} primary={primary} backgroundColor={color}>
          <AppIcon id={iconId} />
        </Avatar>
      ),
    };
  }

  return (
    <MuiCard {...cardProps} className={classes.root}>
      {title && <MuiCardHeader onClick={handleExpand} {...cardHeaderProps} className={classes.header} />}
      <MuiCollapse in={expanded} timeout="auto">
        <MuiCardContent className={classes.content}>{children}</MuiCardContent>
      </MuiCollapse>
      {!_isEmpty(actions) && (
        <MuiCardActions disableSpacing={disableActionsSpacing}>
          <div className="d-flex flex-wrap" style={{ flex: '1 1 auto' }}>
            {actions}
          </div>
          {expandFromActions && (
            <div className="d-flex" style={{ flex: '0 0 auto' }}>
              <IconButton
                className={classnames(classes.expand, {
                  [classes.expandOpen]: expanded,
                })}
                iconId="btnChevronDown"
                onClick={handleExpand}
              />
            </div>
          )}
        </MuiCardActions>
      )}
    </MuiCard>
  );
};

Card.propTypes = {
  defaultBackground: PropTypes.bool,
  padding: PropTypes.oneOf(['none', 'topOnly', 'disableTop', 'dense', 'normal']),
  rootPaddingX: PropTypes.string,
  stretch: PropTypes.bool,
  outlined: PropTypes.bool,
  icon: PropTypes.shape({
    id: PropTypes.string.isRequired,
    primary: PropTypes.bool,
    color: PropTypes.string,
  }),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  stickyHeader: PropTypes.shape({
    top: PropTypes.number.isRequired,
  }),
  denseHeader: PropTypes.bool,
  defaultHeader: PropTypes.bool,
  headerAvatar: PropTypes.node,
  headerAction: PropTypes.node,
  actions: PropTypes.arrayOf(PropTypes.node),
  expandFromActions: PropTypes.bool,
  disableActionsSpacing: PropTypes.bool,
  contentOptions: PropTypes.shape({
    expandable: PropTypes.bool,
    expanded: PropTypes.bool,
  }),
  onExpand: PropTypes.func,
  children: PropTypes.node.isRequired,
};

Card.defaultProps = {
  defaultBackground: false,
  stretch: false,
  padding: 'normal',
  rootPaddingX: null,
  outlined: false,
  icon: null,
  title: null,
  subtitle: null,
  stickyHeader: null,
  denseHeader: false,
  defaultHeader: false,
  headerAvatar: null,
  headerAction: null,
  actions: null,
  expandFromActions: false,
  disableActionsSpacing: false,
  contentOptions: {
    expandable: false,
    expanded: true,
  },
  onExpand: null,
};

export default React.memo(Card);



// WEBPACK FOOTER //
// ./components/core/Card.js